import { useEffect, useState } from "react";
import { StorageB2ConfigForm, StorageB2MigrationPanel } from "@/components/StorageB2Config";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { HardDrive, Loader2 } from "lucide-react";
import { api, formatFileSize } from "@/lib/api";
import { toast } from "sonner";

/**
 * SuperAdminStorage — pagina dedicata gestione Storage
 * Contiene:
 *   • Widget riepilogo Storage piattaforma (somma quote agenzie)
 *   • Widget occupazione reale GCS (bucket)
 *   • Provider Storage attivo (GCS / Wasabi) con credenziali
 *   • Dialog Orfani GCS (scan + delete)
 */
const PROVIDER_LABELS = { gcs: "Google Cloud Storage", wasabi: "Wasabi", b2: "Backblaze B2" };

export default function SuperAdminStorage() {
  // Lista agenzie (per totali aggregati)
  const [list, setList] = useState([]);
  const [listLoading, setListLoading] = useState(true);

  // GCS usage
  const [gcsUsage, setGcsUsage] = useState(null);
  const [gcsLoading, setGcsLoading] = useState(false);

  // Wasabi usage
  const [wasabiUsage, setWasabiUsage] = useState(null);
  const [wasabiLoading, setWasabiLoading] = useState(false);

  // Storage provider config
  const [storageProvider, setStorageProvider] = useState("gcs");
  const [wasabiCfg, setWasabiCfg] = useState({ bucket: "", access_key: "", secret_key: "", endpoint: "", region: "" });
  const [wasabiSecretSet, setWasabiSecretSet] = useState(false);
  const [b2Cfg, setB2Cfg] = useState({ endpoint: "", region: "", bucket_name: "", key_id: "", application_key: "" });
  const [b2Set, setB2Set] = useState({ key_id: false, application_key: false });
  const [savedProvider, setSavedProvider] = useState("gcs");
  const [providerStatus, setProviderStatus] = useState(null);
  const [testingConn, setTestingConn] = useState(false);
  const [b2Usage, setB2Usage] = useState(null);
  const [b2Loading, setB2Loading] = useState(false);
  const [storageCfgLoading, setStorageCfgLoading] = useState(false);
  const [storageCfgSaving, setStorageCfgSaving] = useState(false);

  // Orfani GCS + Wasabi (dialog condiviso, parametrizzato dal provider)
  const [orphansDialogOpen, setOrphansDialogOpen] = useState(false);
  const [orphansProvider, setOrphansProvider] = useState("gcs"); // "gcs" | "wasabi"
  const [orphansData, setOrphansData] = useState(null);
  const [orphansLoading, setOrphansLoading] = useState(false);
  const [orphanDeleting, setOrphanDeleting] = useState(null);
  const [selectedOrphans, setSelectedOrphans] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);

  // ------------- Loaders -------------
  const loadAgencies = async () => {
    setListLoading(true);
    try {
      const { data } = await api.get("/superadmin/agencies");
      setList(Array.isArray(data) ? data : (data?.items || []));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore caricamento agenzie");
    } finally {
      setListLoading(false);
    }
  };

  const loadGcsUsage = async () => {
    setGcsLoading(true);
    try {
      const { data } = await api.get("/admin/storage/gcs-usage");
      setGcsUsage(data);
    } catch (e) {
      setGcsUsage({ error: e?.response?.data?.detail || "Errore caricamento" });
    } finally {
      setGcsLoading(false);
    }
  };

  const loadWasabiUsage = async () => {
    setWasabiLoading(true);
    try {
      const { data } = await api.get("/admin/storage/wasabi-usage");
      setWasabiUsage(data);
    } catch (e) {
      const status = e?.response?.status;
      // 409 = non configurato → non è un errore da mostrare
      setWasabiUsage(status === 409 ? { not_configured: true } : { error: e?.response?.data?.detail || "Errore caricamento" });
    } finally {
      setWasabiLoading(false);
    }
  };

  const loadB2Usage = async () => {
    setB2Loading(true);
    try {
      const { data } = await api.get("/admin/storage/b2-usage");
      setB2Usage(data);
    } catch (e) {
      setB2Usage(e?.response?.status === 409 ? { not_configured: true } : { error: e?.response?.data?.detail || "Errore caricamento" });
    } finally {
      setB2Loading(false);
    }
  };

  const loadProviderStatus = async () => {
    try {
      const { data } = await api.get("/admin/storage/status");
      setProviderStatus(data);
    } catch { setProviderStatus(null); }
  };

  const testConnection = async (prov) => {
    setTestingConn(true);
    try {
      const { data } = await api.post(`/admin/storage/test-connection?provider=${prov}`);
      if (data.connected) toast.success(`${PROVIDER_LABELS[prov]}: connessione riuscita ✓`);
      else toast.error(data.error || (data.configured ? "Connessione non riuscita" : `${PROVIDER_LABELS[prov]} non configurato completamente`));
      if (data.warning) toast.warning(data.warning, { duration: 10000 });
      loadProviderStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore verifica connessione");
    } finally {
      setTestingConn(false);
    }
  };

  const loadStorageConfig = async () => {
    setStorageCfgLoading(true);
    try {
      const { data } = await api.get("/admin/storage/config");
      setStorageProvider(data.provider || "gcs");
      setSavedProvider(data.provider || "gcs");
      const b = data.b2 || {};
      setB2Cfg({ endpoint: b.endpoint || "", region: b.region || "", bucket_name: b.bucket_name || "", key_id: "", application_key: "" });
      setB2Set({ key_id: !!b.key_id_set, application_key: !!b.application_key_set });
      const w = data.wasabi || {};
      setWasabiCfg({
        bucket: w.bucket || "",
        access_key: w.access_key || "",
        secret_key: "",
        endpoint: w.endpoint || "",
        region: w.region || "",
      });
      setWasabiSecretSet(!!w.secret_key_set);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore caricamento config storage");
    } finally {
      setStorageCfgLoading(false);
    }
  };

  const saveStorageConfig = async () => {
    setStorageCfgSaving(true);
    try {
      const { data } = await api.put("/admin/storage/config", {
        provider: storageProvider,
        wasabi: wasabiCfg,
        b2: b2Cfg,
      });
      if (data.warning) toast.warning(data.warning);
      else toast.success(`Provider storage aggiornato: ${data.provider}`);
      loadStorageConfig();
      loadProviderStatus();
      if (data.provider === "b2") loadB2Usage();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore salvataggio");
    } finally {
      setStorageCfgSaving(false);
    }
  };

  const scanOrphans = async (provider = "gcs") => {
    setOrphansProvider(provider);
    setOrphansLoading(true);
    setOrphansDialogOpen(true);
    setSelectedOrphans(new Set());
    try {
      const endpoint = `/admin/storage/${provider}-orphans`;
      const { data } = await api.get(endpoint, { params: { limit: 500 } });
      setOrphansData(data);
    } catch (e) {
      setOrphansData({ error: e?.response?.data?.detail || "Errore" });
    } finally {
      setOrphansLoading(false);
    }
  };

  const deleteOrphan = async (path) => {
    if (!window.confirm(`Cancellare definitivamente questo file da ${PROVIDER_LABELS[orphansProvider]}?\n\n${path}`)) return;
    setOrphanDeleting(path);
    try {
      const endpoint = `/admin/storage/${orphansProvider}-delete-orphan`;
      await api.post(endpoint, { path });
      toast.success(`File cancellato da ${PROVIDER_LABELS[orphansProvider]} ✓`);
      setOrphansData((prev) => prev ? {
        ...prev,
        orphans: (prev.orphans || []).filter((o) => o.path !== path),
        orphan_count: Math.max(0, (prev.orphan_count || 1) - 1),
        total_orphan_bytes: Math.max(0, (prev.total_orphan_bytes || 0) - ((prev.orphans || []).find((o) => o.path === path)?.size || 0)),
      } : prev);
      if (orphansProvider === "wasabi") loadWasabiUsage();
      if (orphansProvider === "b2") loadB2Usage();
      else loadGcsUsage();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore cancellazione");
    } finally {
      setOrphanDeleting(null);
    }
  };

  const toggleOrphanSelection = (path) => {
    setSelectedOrphans((prev) => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path); else next.add(path);
      return next;
    });
  };

  const toggleSelectAllOrphans = () => {
    if (!orphansData?.orphans) return;
    setSelectedOrphans((prev) => {
      if (prev.size === orphansData.orphans.length) return new Set();
      return new Set(orphansData.orphans.map((o) => o.path));
    });
  };

  const deleteOrphansBulk = async (mode) => {
    if (mode === "selected" && selectedOrphans.size === 0) return;
    const count = mode === "all" ? (orphansData?.orphan_count || 0) : selectedOrphans.size;
    const providerLabel = PROVIDER_LABELS[orphansProvider];
    if (!window.confirm(
      mode === "all"
        ? `⚠ ATTENZIONE: cancellare TUTTI i ${count} file orfani da ${providerLabel}?\n\nQuesta operazione è IRREVERSIBILE.`
        : `Cancellare ${count} file selezionati da ${providerLabel}?\n\nOperazione irreversibile.`
    )) return;
    setBulkDeleting(true);
    try {
      const body = mode === "all" ? { all: true } : { paths: Array.from(selectedOrphans) };
      const endpoint = `/admin/storage/${orphansProvider}-delete-orphans-bulk`;
      const { data } = await api.post(endpoint, body);
      toast.success(`${data.deleted_count} file cancellati · ${data.skipped_referenced_count} saltati (referenziati) · ${data.errors_count} errori`);
      setSelectedOrphans(new Set());
      await scanOrphans(orphansProvider);
      if (orphansProvider === "wasabi") loadWasabiUsage();
      if (orphansProvider === "b2") loadB2Usage();
      else loadGcsUsage();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore cancellazione bulk");
    } finally {
      setBulkDeleting(false);
    }
  };

  useEffect(() => {
    loadAgencies();
    loadGcsUsage();
    loadWasabiUsage();
    loadB2Usage();
    loadProviderStatus();
    loadStorageConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ------------- Derived / helpers -------------
  const fmt = (mb) => mb >= 1024 ? `${(mb / 1024).toFixed(2)} GB` : `${mb.toFixed(1)} MB`;

  // Totali piattaforma
  const totals = (list || []).reduce(
    (acc, a) => {
      acc.limitMb += Number(a.storage_limit_mb || 0);
      acc.usedMb += Number(a.storage_used_mb || 0);
      return acc;
    },
    { limitMb: 0, usedMb: 0 },
  );
  const availableMb = Math.max(0, totals.limitMb - totals.usedMb);
  const pct = totals.limitMb > 0 ? (totals.usedMb / totals.limitMb) * 100 : 0;
  const barClass = pct >= 95 ? "bg-red-500" : pct >= 80 ? "bg-amber-500" : "bg-emerald-500";

  // GCS reale
  const usage = gcsUsage || {};
  const totalMb = Number(usage.total_mb || 0);
  const count = Number(usage.count || 0);
  const byKind = usage.by_kind || {};
  const kinds = Object.entries(byKind).sort((a, b) => (b[1]?.bytes || 0) - (a[1]?.bytes || 0));
  const alertLevel = usage.alert_level || "ok";
  const alertPct = Number(usage.alert_pct || 0);
  const thresholdGb = Number(usage.alert_threshold_gb || 0);
  const allocatedMb = (list || []).reduce((acc, a) => acc + Number(a.storage_limit_mb || 0), 0);
  const soldUsedMb = (list || []).reduce((acc, a) => acc + Number(a.storage_used_mb || 0), 0);
  const overhead = totalMb - soldUsedMb;
  const gcsBarClass = alertLevel === "critical" ? "bg-red-500" : alertLevel === "warning" ? "bg-amber-500" : "bg-sky-500";
  const alertMsg = alertLevel === "critical"
    ? `⚠️ ATTENZIONE: superata la soglia di allarme (${thresholdGb} GB) — considera cleanup o aumento quota.`
    : alertLevel === "warning"
    ? `⚠️ Utilizzo elevato: ${alertPct.toFixed(1)}% della soglia di allarme (${thresholdGb} GB).`
    : null;

  const providerBadge = (prov) => {
    if (savedProvider === prov) {
      return <span className="text-[10px] font-bold uppercase tracking-wide bg-emerald-600 text-white px-1.5 py-0.5 rounded" data-testid={`sa-provider-active-${prov}`}>Provider attivo</span>;
    }
    const st = providerStatus?.[prov];
    if (st?.configured) {
      return <span className="text-[10px] text-slate-500 italic" data-testid={`sa-provider-configured-${prov}`}>configurato, non attivo{st.connected === false && prov !== "gcs" ? " · non raggiungibile" : ""}</span>;
    }
    return null;
  };

  const renderS3UsageCard = (prov, usage, loadingFlag, reload) => {
        const wu = usage;
        const wTotalMb = Number(wu.total_mb || 0);
        const wCount = Number(wu.count || 0);
        const wByKind = wu.by_kind || {};
        const wKinds = Object.entries(wByKind).sort((a, b) => (b[1]?.bytes || 0) - (a[1]?.bytes || 0));
        const wAlertLevel = wu.alert_level || "ok";
        const wAlertPct = Number(wu.alert_pct || 0);
        const wThresholdGb = Number(wu.alert_threshold_gb || 0);
        const wBarClass = wAlertLevel === "critical" ? "bg-red-500" : wAlertLevel === "warning" ? "bg-amber-500" : "bg-emerald-500";
        const wAlertMsg = wAlertLevel === "critical"
          ? `⚠️ ATTENZIONE: superata la soglia di allarme (${wThresholdGb} GB) — considera cleanup o aumento quota.`
          : wAlertLevel === "warning"
          ? `⚠️ Utilizzo elevato: ${wAlertPct.toFixed(1)}% della soglia di allarme (${wThresholdGb} GB).`
          : null;
        return (
          <Card className={`${wAlertLevel === "critical" ? "border-red-300 bg-gradient-to-r from-red-50/60" : wAlertLevel === "warning" ? "border-amber-300 bg-gradient-to-r from-amber-50/60" : "border-emerald-200 bg-gradient-to-r from-emerald-50/60"} via-white to-teal-50/40`} data-testid={`sa-${prov}-usage`}>
            <CardContent className="pt-4 pb-4">
              <div className="flex items-start justify-between flex-wrap gap-3 mb-3">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-emerald-700 font-semibold">{prov === "b2" ? "🅱️ Backblaze B2" : "🪣 Wasabi"} — occupazione reale</div>
                  <div className="text-lg font-semibold text-slate-800">Bucket <span className="font-mono text-emerald-700">{wu.bucket || "…"}</span></div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {wu.error ? <span className="text-red-600">⚠️ {wu.error}</span> : <>Endpoint <span className="font-mono">{wu.endpoint || "—"}</span> · Region <span className="font-mono">{wu.region || "—"}</span> · {wCount} oggetti totali {wu.truncated && "(scan interrotto a 100k)"}</>}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 text-right">
                  <div>
                    <div className="text-[10px] uppercase text-slate-500">Utilizzato</div>
                    <div className="text-lg font-semibold text-emerald-700 tabular-nums" data-testid={`sa-${prov}-used`}>
                      {loadingFlag ? <Loader2 className="h-4 w-4 animate-spin inline" /> : wu.error ? "—" : fmt(wTotalMb)}
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase text-slate-500">Oggetti</div>
                    <div className="text-lg font-semibold text-slate-800 tabular-nums" data-testid={`sa-${prov}-count`}>
                      {loadingFlag ? "…" : wCount}
                    </div>
                  </div>
                </div>
              </div>

              {wThresholdGb > 0 && !wu.error && (
                <div className="mb-2">
                  <div className="h-2 rounded-full bg-slate-200 overflow-hidden">
                    <div className={`h-full transition-all ${wBarClass}`} style={{ width: `${Math.min(100, wAlertPct)}%` }} data-testid={`sa-${prov}-bar`} />
                  </div>
                  <div className="mt-1 text-[11px] text-slate-500 flex items-center justify-between">
                    <span>Soglia allarme: <strong className="text-slate-700">{wThresholdGb} GB</strong> · Utilizzo: <strong className={wAlertLevel === "critical" ? "text-red-600" : wAlertLevel === "warning" ? "text-amber-700" : "text-emerald-700"} data-testid={`sa-${prov}-alert-pct`}>{wAlertPct.toFixed(2)}%</strong></span>
                    {wAlertMsg && <span className={wAlertLevel === "critical" ? "text-red-600 font-medium" : "text-amber-700 font-medium"} data-testid={`sa-${prov}-alert-msg`}>{wAlertMsg}</span>}
                  </div>
                </div>
              )}

              {wKinds.length > 0 && (
                <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {wKinds.slice(0, 8).map(([kind, v]) => (
                    <div key={kind} className="bg-white border border-emerald-100 rounded-md px-2 py-1.5" data-testid={`sa-${prov}-kind-${kind}`}>
                      <div className="text-[10px] uppercase text-slate-500 truncate">{kind}</div>
                      <div className="text-sm font-semibold text-slate-800 tabular-nums">{fmt((v.bytes || 0) / (1024 * 1024))}</div>
                      <div className="text-[10px] text-slate-400">{v.count} file</div>
                    </div>
                  ))}
                </div>
              )}

              <div className="mt-2 flex items-center justify-between text-[11px] text-slate-500">
                <span>Dato letto in tempo reale dal bucket {prov === "b2" ? "Backblaze B2" : "Wasabi"} (non dipende da MongoDB).</span>
                <div className="flex items-center gap-3">
                  <button type="button" onClick={() => scanOrphans(prov)} className="text-amber-700 hover:text-amber-900 font-medium" data-testid={`sa-${prov}-scan-orphans`}>
                    🧹 Trova orfani
                  </button>
                  <button type="button" onClick={reload} disabled={loadingFlag} className="text-emerald-700 hover:text-emerald-900 font-medium disabled:opacity-50" data-testid={`sa-${prov}-refresh`}>
                    {loadingFlag ? "…" : "🔄 Aggiorna"}
                  </button>
                </div>
              </div>
            </CardContent>
          </Card>
        );
  };

  return (
    <div className="p-4 sm:p-6 space-y-5" data-testid="sa-storage-page">
      <div className="flex items-center gap-2">
        <HardDrive className="h-5 w-5 text-indigo-600" />
        <h1 className="text-xl sm:text-2xl font-semibold text-slate-800">Storage</h1>
        {listLoading && <Loader2 className="h-4 w-4 animate-spin text-slate-400" />}
      </div>
      <p className="text-sm text-slate-500 -mt-3">Gestione centralizzata provider storage, occupazione bucket e manutenzione orfani.</p>

      {/* Widget riepilogo Storage globale */}
      <Card className="border-indigo-200 bg-gradient-to-r from-indigo-50/60 via-white to-violet-50/40" data-testid="sa-storage-overview">
        <CardContent className="pt-4 pb-4">
          <div className="flex items-start justify-between flex-wrap gap-3 mb-3">
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-indigo-700 font-semibold">💾 Storage piattaforma</div>
              <div className="text-lg font-semibold text-slate-800">Spazio totale a disposizione</div>
              <div className="text-xs text-slate-500 mt-0.5">Somma dei limiti di tutte le {list?.length ?? 0} agenzie visibili.</div>
            </div>
            <div className="grid grid-cols-3 gap-3 text-right">
              <div>
                <div className="text-[10px] uppercase text-slate-500">Assegnato</div>
                <div className="text-lg font-semibold text-indigo-700 tabular-nums" data-testid="sa-storage-allocated">{fmt(totals.limitMb)}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase text-slate-500">Utilizzato</div>
                <div className="text-lg font-semibold text-slate-800 tabular-nums" data-testid="sa-storage-used">{fmt(totals.usedMb)}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase text-slate-500">Disponibile</div>
                <div className="text-lg font-semibold text-emerald-700 tabular-nums" data-testid="sa-storage-available">{fmt(availableMb)}</div>
              </div>
            </div>
          </div>
          <div className="h-2 rounded-full bg-slate-200 overflow-hidden">
            <div className={`h-full transition-all ${barClass}`} style={{ width: `${Math.min(100, pct)}%` }} />
          </div>
          <div className="mt-1 text-[11px] text-slate-500 flex items-center justify-between">
            <span>Utilizzo complessivo: <strong className={pct >= 95 ? "text-red-600" : pct >= 80 ? "text-amber-700" : "text-emerald-700"}>{pct.toFixed(1)}%</strong></span>
            {pct >= 80 && (
              <span className={pct >= 95 ? "text-red-600" : "text-amber-700"}>
                ⚠️ Alcune agenzie potrebbero raggiungere presto il limite
              </span>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Widget GCS reale */}
      <Card className={`${alertLevel === "critical" ? "border-red-300 bg-gradient-to-r from-red-50/60" : alertLevel === "warning" ? "border-amber-300 bg-gradient-to-r from-amber-50/60" : "border-sky-200 bg-gradient-to-r from-sky-50/60"} via-white to-blue-50/40`} data-testid="sa-gcs-usage">
        <CardContent className="pt-4 pb-4">
          <div className="flex items-start justify-between flex-wrap gap-3 mb-3">
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-sky-700 font-semibold">☁️ Google Cloud Storage — occupazione reale</div>
              <div className="text-lg font-semibold text-slate-800">Bucket <span className="font-mono text-sky-700">{usage.bucket || "…"}</span></div>
              <div className="text-xs text-slate-500 mt-0.5">
                {usage.error ? <span className="text-red-600">⚠️ {usage.error}</span> : <>Project <span className="font-mono">{usage.project_id || "—"}</span> · {count} oggetti totali {usage.truncated && "(scan interrotto a 100k)"}</>}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 text-right">
              <div>
                <div className="text-[10px] uppercase text-slate-500">Utilizzato</div>
                <div className="text-lg font-semibold text-sky-700 tabular-nums" data-testid="sa-gcs-used">
                  {gcsLoading ? <Loader2 className="h-4 w-4 animate-spin inline" /> : usage.error ? "—" : fmt(totalMb)}
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase text-slate-500">Oggetti</div>
                <div className="text-lg font-semibold text-slate-800 tabular-nums" data-testid="sa-gcs-count">
                  {gcsLoading ? "…" : count}
                </div>
              </div>
            </div>
          </div>

          {thresholdGb > 0 && !usage.error && (
            <div className="mb-2">
              <div className="h-2 rounded-full bg-slate-200 overflow-hidden">
                <div className={`h-full transition-all ${gcsBarClass}`} style={{ width: `${Math.min(100, alertPct)}%` }} data-testid="sa-gcs-bar" />
              </div>
              <div className="mt-1 text-[11px] text-slate-500 flex items-center justify-between">
                <span>Soglia allarme: <strong className="text-slate-700">{thresholdGb} GB</strong> · Utilizzo: <strong className={alertLevel === "critical" ? "text-red-600" : alertLevel === "warning" ? "text-amber-700" : "text-sky-700"} data-testid="sa-gcs-alert-pct">{alertPct.toFixed(2)}%</strong></span>
                {alertMsg && <span className={alertLevel === "critical" ? "text-red-600 font-medium" : "text-amber-700 font-medium"} data-testid="sa-gcs-alert-msg">{alertMsg}</span>}
              </div>
            </div>
          )}

          {kinds.length > 0 && (
            <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-2">
              {kinds.slice(0, 8).map(([kind, v]) => (
                <div key={kind} className="bg-white border border-sky-100 rounded-md px-2 py-1.5" data-testid={`sa-gcs-kind-${kind}`}>
                  <div className="text-[10px] uppercase text-slate-500 truncate">{kind}</div>
                  <div className="text-sm font-semibold text-slate-800 tabular-nums">{fmt((v.bytes || 0) / (1024 * 1024))}</div>
                  <div className="text-[10px] text-slate-400">{v.count} file</div>
                </div>
              ))}
            </div>
          )}

          {!usage.error && (
            <div className="mt-3 pt-3 border-t border-sky-100 grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs" data-testid="sa-gcs-reconciliation">
              <div className="bg-indigo-50/60 border border-indigo-100 rounded-md px-2 py-1.5">
                <div className="text-[10px] uppercase text-indigo-600">Allocato a agenzie</div>
                <div className="text-sm font-semibold text-indigo-800 tabular-nums">{fmt(allocatedMb)}</div>
                <div className="text-[10px] text-indigo-500">quota commerciale</div>
              </div>
              <div className="bg-emerald-50/60 border border-emerald-100 rounded-md px-2 py-1.5">
                <div className="text-[10px] uppercase text-emerald-600">Consumato (Mongo)</div>
                <div className="text-sm font-semibold text-emerald-800 tabular-nums">{fmt(soldUsedMb)}</div>
                <div className="text-[10px] text-emerald-500">contabilizzato per piano</div>
              </div>
              <div className="bg-sky-50/60 border border-sky-100 rounded-md px-2 py-1.5">
                <div className="text-[10px] uppercase text-sky-700">Reale su GCS</div>
                <div className="text-sm font-semibold text-sky-800 tabular-nums">{fmt(totalMb)}</div>
                <div className="text-[10px] text-sky-500">fatturato da Google</div>
              </div>
              <div className={`${overhead > 0 ? "bg-amber-50/60 border-amber-200" : "bg-slate-50/60 border-slate-200"} border rounded-md px-2 py-1.5`}>
                <div className={`text-[10px] uppercase ${overhead > 0 ? "text-amber-700" : "text-slate-600"}`}>Differenza</div>
                <div className={`text-sm font-semibold tabular-nums ${overhead > 0 ? "text-amber-800" : "text-slate-800"}`}>
                  {overhead >= 0 ? "+" : ""}{fmt(Math.abs(overhead))}
                </div>
                <div className="text-[10px] text-slate-500">{overhead > 0 ? "orfani / non contati" : "in linea"}</div>
              </div>
            </div>
          )}

          <div className="mt-2 flex items-center justify-between text-[11px] text-slate-500">
            <span>Dato letto in tempo reale dal bucket (non dipende da MongoDB).</span>
            <div className="flex items-center gap-3">
              <button type="button" onClick={() => scanOrphans("gcs")} className="text-amber-700 hover:text-amber-900 font-medium" data-testid="sa-gcs-scan-orphans">
                🧹 Trova orfani
              </button>
              <button type="button" onClick={loadGcsUsage} disabled={gcsLoading} className="text-sky-700 hover:text-sky-900 font-medium disabled:opacity-50" data-testid="sa-gcs-refresh">
                {gcsLoading ? "…" : "🔄 Aggiorna"}
              </button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Widget occupazione reale Wasabi / B2 — visibili solo se configurati */}
      {wasabiUsage && !wasabiUsage.not_configured && renderS3UsageCard("wasabi", wasabiUsage, wasabiLoading, loadWasabiUsage)}
      {b2Usage && !b2Usage.not_configured && renderS3UsageCard("b2", b2Usage, b2Loading, loadB2Usage)}

      {/* Provider Storage (GCS / Wasabi) */}
      <Card className="border-indigo-200 bg-gradient-to-r from-indigo-50/40 via-white to-slate-50/40" data-testid="sa-storage-provider-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <span>💾 Provider Storage attivo</span>
            {storageCfgLoading && <Loader2 className="h-4 w-4 animate-spin text-slate-400" />}
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-2 space-y-3">
          <div className="flex flex-wrap items-center gap-3">
            <label className={`flex items-center gap-2 px-3 py-2 rounded-md border cursor-pointer transition ${storageProvider === "gcs" ? "border-indigo-400 bg-indigo-50 ring-2 ring-indigo-200" : "border-slate-200 bg-white hover:bg-slate-50"}`} data-testid="sa-storage-provider-gcs">
              <input type="radio" name="storage-provider" value="gcs" checked={storageProvider === "gcs"} onChange={() => setStorageProvider("gcs")} className="accent-indigo-600" />
              <span className="text-sm font-medium">☁️ Google Cloud Storage</span>
              {providerBadge("gcs")}
            </label>
            <label className={`flex items-center gap-2 px-3 py-2 rounded-md border cursor-pointer transition ${storageProvider === "wasabi" ? "border-emerald-400 bg-emerald-50 ring-2 ring-emerald-200" : "border-slate-200 bg-white hover:bg-slate-50"}`} data-testid="sa-storage-provider-wasabi">
              <input type="radio" name="storage-provider" value="wasabi" checked={storageProvider === "wasabi"} onChange={() => setStorageProvider("wasabi")} className="accent-emerald-600" />
              <span className="text-sm font-medium">🪣 Wasabi (S3-compatible)</span>
              {providerBadge("wasabi")}
            </label>
            <label className={`flex items-center gap-2 px-3 py-2 rounded-md border cursor-pointer transition ${storageProvider === "b2" ? "border-orange-400 bg-orange-50 ring-2 ring-orange-200" : "border-slate-200 bg-white hover:bg-slate-50"}`} data-testid="sa-storage-provider-b2">
              <input type="radio" name="storage-provider" value="b2" checked={storageProvider === "b2"} onChange={() => setStorageProvider("b2")} className="accent-orange-600" />
              <span className="text-sm font-medium">🅱️ Backblaze B2</span>
              {providerBadge("b2")}
            </label>
          </div>
          {storageProvider !== savedProvider && (
            <p className="text-[11px] text-amber-700" data-testid="sa-storage-provider-pending">
              Stai selezionando {PROVIDER_LABELS[storageProvider]}: diventerà il provider attivo per i NUOVI upload solo dopo “Salva configurazione”. I file esistenti restano leggibili dal loro storage.
            </p>
          )}
          {storageProvider === "b2" && (
            <>
              <StorageB2ConfigForm cfg={b2Cfg} setCfg={setB2Cfg} secretsSet={b2Set} onTest={() => testConnection("b2")} testing={testingConn} />
              {providerStatus?.b2?.error && <p className="text-[11px] text-red-600" data-testid="sa-b2-status-error">{providerStatus.b2.error}</p>}
              {providerStatus?.b2?.warning && <p className="text-[11px] text-red-600" data-testid="sa-b2-status-warning">⚠️ {providerStatus.b2.warning}</p>}
              <StorageB2MigrationPanel />
            </>
          )}
          {storageProvider === "wasabi" && (
            <div className="rounded-md border border-emerald-200 bg-white p-3 space-y-3" data-testid="sa-wasabi-config">
              <p className="text-[11px] text-slate-500">
                Compila i campi Wasabi. I valori vengono salvati preservando maiuscole/minuscole originali.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-slate-700">Nome bucket</label>
                  <input
                    type="text"
                    value={wasabiCfg.bucket}
                    onChange={(e) => setWasabiCfg((c) => ({ ...c, bucket: e.target.value }))}
                    placeholder="es. my-crm-bucket"
                    className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm font-mono focus:ring-2 focus:ring-emerald-200 focus:border-emerald-400"
                    autoComplete="off"
                    autoCapitalize="off"
                    autoCorrect="off"
                    spellCheck={false}
                    data-testid="sa-wasabi-bucket"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-700">Access Key</label>
                  <input
                    type="text"
                    value={wasabiCfg.access_key}
                    onChange={(e) => setWasabiCfg((c) => ({ ...c, access_key: e.target.value }))}
                    placeholder="AKIA…"
                    className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm font-mono focus:ring-2 focus:ring-emerald-200 focus:border-emerald-400"
                    autoComplete="off"
                    autoCapitalize="off"
                    autoCorrect="off"
                    spellCheck={false}
                    data-testid="sa-wasabi-access-key"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="text-xs font-medium text-slate-700 flex items-center gap-2">
                    Secret Key
                    {wasabiSecretSet && <span className="text-[10px] font-normal text-emerald-700">✓ già impostata — lascia vuoto per non cambiarla</span>}
                  </label>
                  <input
                    type="password"
                    value={wasabiCfg.secret_key}
                    onChange={(e) => setWasabiCfg((c) => ({ ...c, secret_key: e.target.value }))}
                    placeholder={wasabiSecretSet ? "•••••••••••••••• (invariata)" : "wJalrXUtnFEMI/K7MDENG/…"}
                    className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm font-mono focus:ring-2 focus:ring-emerald-200 focus:border-emerald-400"
                    autoComplete="new-password"
                    autoCapitalize="off"
                    autoCorrect="off"
                    spellCheck={false}
                    data-testid="sa-wasabi-secret-key"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-700">Endpoint</label>
                  <input
                    type="text"
                    value={wasabiCfg.endpoint}
                    onChange={(e) => setWasabiCfg((c) => ({ ...c, endpoint: e.target.value }))}
                    placeholder="https://s3.wasabisys.com"
                    className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm font-mono focus:ring-2 focus:ring-emerald-200 focus:border-emerald-400"
                    autoComplete="off"
                    autoCapitalize="off"
                    autoCorrect="off"
                    spellCheck={false}
                    data-testid="sa-wasabi-endpoint"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-700">Regione</label>
                  <input
                    type="text"
                    value={wasabiCfg.region}
                    onChange={(e) => setWasabiCfg((c) => ({ ...c, region: e.target.value }))}
                    placeholder="eu-central-1"
                    className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm font-mono focus:ring-2 focus:ring-emerald-200 focus:border-emerald-400"
                    autoComplete="off"
                    autoCapitalize="off"
                    autoCorrect="off"
                    spellCheck={false}
                    data-testid="sa-wasabi-region"
                  />
                </div>
              </div>
            </div>
          )}
          <div className="flex items-center justify-end pt-1">
            <Button
              onClick={saveStorageConfig}
              disabled={storageCfgSaving || storageCfgLoading}
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
              data-testid="sa-storage-save"
            >
              {storageCfgSaving ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Salvataggio…</> : "Salva configurazione"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Dialog Orfani GCS */}
      <Dialog open={orphansDialogOpen} onOpenChange={setOrphansDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="sa-gcs-orphans-dialog">
          <DialogHeader>
            <DialogTitle>🧹 Orfani su {PROVIDER_LABELS[orphansProvider]}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {orphansLoading ? (
              <div className="py-8 text-center text-slate-500">
                <Loader2 className="h-6 w-6 animate-spin inline mr-2" /> Scansione in corso…
              </div>
            ) : orphansData?.error ? (
              <div className="p-3 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm">
                ⚠️ {orphansData.error}
              </div>
            ) : orphansData ? (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                  <div className="bg-slate-50 border rounded-md px-2 py-1.5">
                    <div className="text-[10px] uppercase text-slate-500">Blob scansionati</div>
                    <div className="text-sm font-semibold tabular-nums">{orphansData.scanned}</div>
                  </div>
                  <div className="bg-slate-50 border rounded-md px-2 py-1.5">
                    <div className="text-[10px] uppercase text-slate-500">Riferimenti in Mongo</div>
                    <div className="text-sm font-semibold tabular-nums">{orphansData.known_refs}</div>
                  </div>
                  <div className={`${orphansData.orphan_count > 0 ? "bg-amber-50 border-amber-200" : "bg-emerald-50 border-emerald-200"} border rounded-md px-2 py-1.5`}>
                    <div className="text-[10px] uppercase text-slate-500">Orfani trovati</div>
                    <div className={`text-sm font-semibold tabular-nums ${orphansData.orphan_count > 0 ? "text-amber-800" : "text-emerald-800"}`} data-testid="sa-gcs-orphan-count">{orphansData.orphan_count}</div>
                  </div>
                  <div className={`${orphansData.total_orphan_mb > 0 ? "bg-amber-50 border-amber-200" : "bg-slate-50 border-slate-200"} border rounded-md px-2 py-1.5`}>
                    <div className="text-[10px] uppercase text-slate-500">Recupero potenziale</div>
                    <div className={`text-sm font-semibold tabular-nums ${orphansData.total_orphan_mb > 0 ? "text-amber-800" : "text-slate-700"}`}>{orphansData.total_orphan_mb} MB</div>
                  </div>
                </div>

                {orphansData.orphan_count === 0 ? (
                  <div className="p-4 rounded-md bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm text-center">
                    ✅ Nessun orfano su GCS — tutti i file sono referenziati in database.
                  </div>
                ) : (
                  <>
                    <div className="flex items-center justify-between gap-2 p-2 rounded-md bg-amber-50 border border-amber-200 text-xs">
                      <div className="flex items-center gap-3">
                        <span className="text-slate-700">
                          <strong>{selectedOrphans.size}</strong> / {orphansData.orphan_count} selezionati
                        </span>
                        <button type="button" onClick={toggleSelectAllOrphans} className="text-sky-700 hover:text-sky-900 font-medium" data-testid="sa-gcs-select-all">
                          {selectedOrphans.size === orphansData.orphans.length ? "Deseleziona tutti" : "Seleziona tutti"}
                        </button>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button size="sm" variant="destructive" onClick={() => deleteOrphansBulk("selected")} disabled={bulkDeleting || selectedOrphans.size === 0} data-testid="sa-gcs-bulk-delete-selected">
                          {bulkDeleting ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : "🗑"} Elimina {selectedOrphans.size} selezionati
                        </Button>
                        <Button size="sm" variant="destructive" onClick={() => deleteOrphansBulk("all")} disabled={bulkDeleting} className="bg-rose-700 hover:bg-rose-800" data-testid="sa-gcs-bulk-delete-all">
                          {bulkDeleting ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : "🔥"} Elimina TUTTI ({orphansData.orphan_count})
                        </Button>
                      </div>
                    </div>

                    <div className="border border-slate-200 rounded-md overflow-hidden max-h-[50vh] overflow-y-auto">
                      <table className="w-full text-xs">
                        <thead className="bg-slate-50 sticky top-0">
                          <tr>
                            <th className="w-8 px-2 py-2">
                              <input
                                type="checkbox"
                                checked={orphansData.orphans.length > 0 && selectedOrphans.size === orphansData.orphans.length}
                                onChange={toggleSelectAllOrphans}
                                data-testid="sa-gcs-header-checkbox"
                              />
                            </th>
                            <th className="text-left px-2 py-2">Path</th>
                            <th className="text-right px-2 py-2">Dimensione</th>
                            <th className="text-left px-2 py-2">Modificato</th>
                            <th className="text-right px-2 py-2">Azione</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(orphansData.orphans || []).map((o) => {
                            const selected = selectedOrphans.has(o.path);
                            return (
                              <tr key={o.path} className={`border-t border-slate-100 hover:bg-slate-50 ${selected ? "bg-sky-50" : ""}`}>
                                <td className="px-2 py-1.5">
                                  <input
                                    type="checkbox"
                                    checked={selected}
                                    onChange={() => toggleOrphanSelection(o.path)}
                                    data-testid={`sa-gcs-row-checkbox-${o.path.slice(-16)}`}
                                  />
                                </td>
                                <td className="px-2 py-1.5 font-mono text-[11px] break-all">{o.path}</td>
                                <td className="px-2 py-1.5 text-right tabular-nums">{formatFileSize(o.size)}</td>
                                <td className="px-2 py-1.5 text-slate-500">{o.updated ? new Date(o.updated).toLocaleDateString("it-IT") : "—"}</td>
                                <td className="px-2 py-1.5 text-right">
                                  <Button size="sm" variant="destructive" onClick={() => deleteOrphan(o.path)} disabled={orphanDeleting === o.path || bulkDeleting} data-testid={`sa-gcs-delete-${o.path.slice(-16)}`}>
                                    {orphanDeleting === o.path ? <Loader2 className="h-3 w-3 animate-spin" /> : "Elimina"}
                                  </Button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    {orphansData.orphan_count >= orphansData.limited_to && (
                      <div className="text-[11px] text-amber-700 bg-amber-50 border rounded px-2 py-1">
                        Elenco troncato a {orphansData.limited_to} risultati per riga. Usa <strong>&quot;Elimina TUTTI&quot;</strong> per rimuovere anche quelli oltre la lista.
                      </div>
                    )}
                  </>
                )}
              </>
            ) : null}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => scanOrphans(orphansProvider)} disabled={orphansLoading}>🔄 Riscansiona</Button>
            <Button variant="outline" onClick={() => setOrphansDialogOpen(false)}>Chiudi</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
